import { MouseEvent, ReactElement, useMemo } from 'react'
import { reject, reverse, sortBy, toNumber } from 'lodash-es'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { Box, Button, Stack, Typography, useTheme } from '@mui/material'
import {
  getGuidToMGroup,
  upsertStoreyAssignment,
} from '@editor/components/Controls/IFCUploadWidget/utils/ifcMetadataUtils'
import { CorneredSection } from '@ui2/structure'
import { roofStoreyKey, useControlStore, useModelStore, useSceneStore } from '@editorStores'
import { useIFCColoring, useIsUpdatingIfcModelMetadata } from '@editorHooks'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import useIFCElementSelection from '../../hooks/useIFCElementSelection'
import { getAlignmentMatrix, getGuidToAlignedBox } from '../../utils/glbElementGeometry'
import IFCMetadataPendingNotice from '../IFCMetadataPendingNotice'
import IFCSelectionInfo from '../IFCSelectionInfo'
import IFCStoreyAssignmentSelect from '../IFCStoreyAssignmentSelect'
import IFCStoreyColorLegend from '../IFCStoreyColorLegend'
import { computeAutoStoreyAssignments } from './autoStoreyAssignment'
import { getGuidToStoreyColor, getStoreyColorByStorey, getStoreyToGuidSet } from './utils'

/** IFC type ("Dachflächen") whose elements are auto-assigned to the roof storey. */
const ROOF_ELEMENT_TYPE = 'roof_slabs'

/**
 * Storey-assignment mode for the IFC Import page: select IFC elements (click or marquee) and assign
 * them to a storey by picking it from the dropdown (assignment happens on change, no confirmation).
 */
const IFCStoreyAssignmentPanel = (): ReactElement => {
  const theme = useTheme()
  const isUpdating = useIsUpdatingIfcModelMetadata()
  const {
    selectedIfcElementGuids,
    setSelectedIfcElementGuids,
    activeGlbFile,
    metadata,
    updateIfcModelMetadata,
  } = useIFCElementSelection()

  const availableStoreys = useModelStore(state => state.availableStoreys)
  const storeyOptions = useMemo(() => {
    const nonRoofStoreys = reverse(
      sortBy(
        reject(Array.from(availableStoreys), storey => storey === roofStoreyKey),
        storey => toNumber(storey),
      ),
    )
    return availableStoreys.has(roofStoreyKey) ? [roofStoreyKey, ...nonRoofStoreys] : nonRoofStoreys
  }, [availableStoreys])

  const hiddenElementIds = useControlStore(state => state.hiddenElementIds)
  const addHiddenElementIds = useControlStore(state => state.addHiddenElementIds)
  const removeHiddenElementIds = useControlStore(state => state.removeHiddenElementIds)
  const applyIfcAlignmentConfig = useControlStore(state => state.applyIfcAlignmentConfig)

  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const loadedGltfModels = useSceneStore(state => state.loadedGltfModels)
  const gltfModel = activeGlbFile ? loadedGltfModels[activeGlbFile.guid] : null

  // Color the IFC model by storey (overriding the default type coloring) while this step is open.
  const storeyColorByStorey = useMemo(() => getStoreyColorByStorey(storeyOptions), [storeyOptions])
  const storeyToGuidSet = useMemo(
    () => getStoreyToGuidSet(metadata?.storeyAssignments ?? []),
    [metadata?.storeyAssignments],
  )
  const guidToStoreyColor = useMemo(
    () => getGuidToStoreyColor(metadata?.storeyAssignments ?? [], storeyColorByStorey),
    [metadata?.storeyAssignments, storeyColorByStorey],
  )
  // Elements without a storey assignment are colored grey (like the outer walls).
  useIFCColoring(guidToStoreyColor, theme.palette.grey[500])

  const storeyColorItems = useMemo(
    () =>
      storeyOptions.map(storey => {
        const storeyGuids = Array.from(storeyToGuidSet[storey] ?? new Set<string>())
        const visible =
          storeyGuids.length === 0 || storeyGuids.some(guid => !hiddenElementIds.has(guid))

        return {
          storey,
          color: storeyColorByStorey[storey],
          visible,
          onToggleVisibility: () => {
            if (storeyGuids.length === 0) return
            if (visible) addHiddenElementIds(storeyGuids)
            else removeHiddenElementIds(storeyGuids)
          },
          onClickSelect: (event: MouseEvent<HTMLButtonElement>) => {
            if (storeyGuids.length === 0) return
            setSelectedIfcElementGuids(
              eventMatchesPointerModifier(event, 'multiSelect')
                ? Array.from(new Set([...selectedIfcElementGuids, ...storeyGuids]))
                : storeyGuids,
            )
          },
        }
      }),
    [
      storeyOptions,
      storeyToGuidSet,
      storeyColorByStorey,
      hiddenElementIds,
      addHiddenElementIds,
      removeHiddenElementIds,
      selectedIfcElementGuids,
      setSelectedIfcElementGuids,
    ],
  )

  const disabled = !activeGlbFile || storeyOptions.length === 0

  const canAutoAssign = !!gltfModel && Object.keys(storeyBoundaries).length > 0

  // Guess the storey of every non-filtered element: place a z-plane at the center of each storey
  // (from the configured storey boundaries) and assign the element to the first storey whose plane
  // lies within the element's z-bounds (from the loaded, aligned GLB).
  const handleAutoAssign = () => {
    if (!gltfModel) return
    const filtered = new Set((metadata?.filteredElements ?? []).flat())
    const guidToMGroup = getGuidToMGroup(metadata)
    const nonFilteredGuids = new Set(Object.keys(guidToMGroup).filter(guid => !filtered.has(guid)))

    const alignmentMatrix = getAlignmentMatrix(
      activeGlbFile?.additional_data?.alignment_config,
      applyIfcAlignmentConfig,
    )
    const guidToBox = getGuidToAlignedBox(gltfModel, nonFilteredGuids, alignmentMatrix)
    const assignments = computeAutoStoreyAssignments(guidToBox, storeyBoundaries, guidToMGroup, {
      type: ROOF_ELEMENT_TYPE,
      storey: roofStoreyKey,
    })
    if (assignments.length === 0) return

    updateIfcModelMetadata(current => {
      if (!current) return current
      return {
        ...current,
        storeyAssignments: assignments.reduce(
          (acc, assignment) => upsertStoreyAssignment(acc, assignment),
          current.storeyAssignments ?? [],
        ),
      }
    })
  }

  if (!metadata)
    return (
      <CorneredSection>
        <IFCMetadataPendingNotice />
      </CorneredSection>
    )

  return (
    <Box>
      <Stack spacing={2}>
        <CorneredSection title="Auswahl">
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Elemente per Klick oder Rahmenauswahl markieren und einem Stockwerk zuweisen.
            </Typography>

            <IFCSelectionInfo showWhenEmpty />

            <IFCStoreyAssignmentSelect
              guids={selectedIfcElementGuids}
              metadata={metadata}
              storeyOptions={storeyOptions}
              disabled={disabled}
              updateIfcModelMetadata={updateIfcModelMetadata}
              onAssigned={() => setSelectedIfcElementGuids([])}
            />

            <Button
              variant="contained"
              size="small"
              startIcon={<AutoFixHighIcon />}
              onClick={handleAutoAssign}
              disabled={disabled || !canAutoAssign || isUpdating}
              data-cy="btn-ifc-auto-storey-assignment"
            >
              Automatische Stockwerkszuweisung
            </Button>
          </Stack>
        </CorneredSection>

        {storeyOptions.length > 0 && (
          <CorneredSection title="Färbung nach Stockwerk" collapsible defaultOpen>
            <IFCStoreyColorLegend items={storeyColorItems} />
          </CorneredSection>
        )}
      </Stack>
    </Box>
  )
}

export default IFCStoreyAssignmentPanel
